import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Link from '../general/Link';
import { UserContext } from '../ContextProviders';
import { useNovaChat } from '@/hooks/useNovaChat';
import { useNova } from './NovaContext';
import NovaMark from './NovaMark';
import NovaBetaTag from './NovaBetaTag';
import NovaAnswerCard from './NovaAnswerCard';
import NovaComposer from './NovaComposer';
import NovaLandingSidebar from './NovaLandingSidebar';
import NovaUploadDialog from './NovaUploadDialog';
import { novaThemes, novaDisclaimer, novaTableVars } from './novaTheme';
import type { NovaChatMessage } from '@/types';

const REPORT_GENERATOR_HREF = '/build-report';
const DISCOVER_HREF = '/discover';

const NovaLanding: React.FC = () => {
  const router = useRouter();
  const user = useContext(UserContext);
  const { theme } = useNova();
  const {
    messages,
    suggestedQuestions,
    waiting,
    streaming,
    conversationId,
    conversations,
    conversationsLoading,
    published12mFilter,
    setPublished12mFilter,
    pendingFilePaths,
    addPendingFilePaths,
    removePendingFilePath,
    sendMessage,
    stopStreaming,
    startNewConversation,
    resumeConversation,
    loadConversations,
    deleteConversations,
  } = useNovaChat();
  const [uploadOpen, setUploadOpen] = useState(false);
  // Drives the hero mark and the last answer's mark: breathing at rest, leans in on
  // the first keystroke, back to breathing once the field is emptied — spec: "Landing
  // page — type to lean in."
  const [hasDraft, setHasDraft] = useState(false);
  const handleDraftChange = useCallback((value: string) => setHasDraft(!!value), []);
  const [uploadInitialFiles, setUploadInitialFiles] = useState<File[]>([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Whether the reader is still following the bottom of the growing answer — set
  // on scroll, and reset whenever a new turn starts. Streaming only keeps
  // auto-scrolling while this stays true, so scrolling up to re-read earlier
  // text isn't yanked back down on the next token.
  const stickToBottomRef = useRef(true);
  const seededFromQueryRef = useRef(false);
  const surfaces = novaThemes[theme];

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lets "Build a Report"/Discover hand off into this page's own useNovaChat
  // instance: ?q= carries a prompt to send fresh, ?c= carries a conversation id to
  // resume (picking a history item from those pages' sidebars can't resume it
  // locally — NovaTemplates/NovaDiscover have their own separate useNovaChat state
  // that's discarded the moment they navigate away). Both fire once, as soon as
  // the router has resolved the query (client-side navigations have it
  // immediately; a hard load resolves it a tick later).
  useEffect(() => {
    if (!router.isReady || seededFromQueryRef.current) return;
    const seededQuery = router.query.q;
    const query = Array.isArray(seededQuery) ? seededQuery[0] : seededQuery;
    const seededConversation = router.query.c;
    const conversationToResume = Array.isArray(seededConversation) ? seededConversation[0] : seededConversation;
    const seededFile = router.query.file;
    const fileToAttach = Array.isArray(seededFile) ? seededFile[0] : seededFile;
    if (query) {
      seededFromQueryRef.current = true;
      sendMessage(query, fileToAttach ? [fileToAttach] : []);
      router.replace(router.pathname, router.asPath.split(/[?#]/)[0], { shallow: true });
    } else if (conversationToResume) {
      seededFromQueryRef.current = true;
      resumeConversation(conversationToResume);
      router.replace(router.pathname, router.asPath.split(/[?#]/)[0], { shallow: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.q, router.query.c]);

  const prevMessageCountRef = useRef(0);
  const prevConversationIdRef = useRef(conversationId);

  useLayoutEffect(() => {
    // `sendMessage` sets `streaming` true and appends the new turn in the same
    // tick, so a naive pair of effects keyed on `messages.length` and on
    // `messages`/`streaming` would both fire on that render — the streaming
    // effect's instant jump cancelling the turn-start effect's smooth one
    // before it can animate. Combined into one effect, only one branch runs.
    // Keyed on `conversationId` too, not just the count: resuming a different
    // conversation with the same number of messages (e.g. from the sidebar)
    // still needs the scroll position reset — it's a new answer even though
    // the length didn't change.
    const isNewTurn = messages.length !== prevMessageCountRef.current || conversationId !== prevConversationIdRef.current;
    prevMessageCountRef.current = messages.length;
    prevConversationIdRef.current = conversationId;

    if (isNewTurn) {
      // A new turn: jump to the top of the fresh answer and start following it —
      // the reader just asked a question, so assume they want to watch it arrive.
      transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      stickToBottomRef.current = true;
      return;
    }

    // While the answer is still streaming in, keep the growing text in view —
    // but only as long as the reader hasn't scrolled away to re-read something
    // earlier. `messages` gets a new reference on every streamed token (each
    // token extends the last message in place), which is exactly what this
    // needs to fire on.
    if (!streaming || !stickToBottomRef.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streaming, conversationId]);

  const handleTranscriptScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 80;
  };

  const openUploadDialog = (files?: File[]) => {
    setUploadInitialFiles(files || []);
    setUploadOpen(true);
  };

  const firstName = user?.name?.trim().split(/\s+/)[0] || 'there';
  // Groups the flat message list into question/answer turns so a follow-up
  // question keeps every earlier turn on screen instead of replacing them —
  // the last turn's `answer` is left undefined while its response is still
  // in flight (no assistant message pushed for it yet).
  const turns = useMemo(() => {
    const result: { question?: string; answer?: NovaChatMessage }[] = [];
    messages.forEach((message) => {
      if (message.role === 'user') {
        result.push({ question: message.content });
      } else if (result.length > 0 && !result[result.length - 1].answer) {
        result[result.length - 1].answer = message;
      } else {
        result.push({ answer: message });
      }
    });
    return result;
  }, [messages]);

  return (
    <div
      className="nova-full-page flex h-[calc(100dvh-var(--header-height))] flex-col gap-0 overflow-hidden md:flex-row"
      style={{ background: surfaces.surfaces.panelBase, color: surfaces.ink.body }}
    >
      <NovaLandingSidebar
        theme={theme}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(true)}
        onExpand={() => setSidebarCollapsed(false)}
        onNewChat={startNewConversation}
        conversations={conversations}
        conversationsLoading={conversationsLoading}
        activeConversationId={conversationId}
        onSelectConversation={resumeConversation}
        onDeleteConversation={(id) => deleteConversations([id])}
        userName={user?.name}
      />

      <div
        className="relative flex min-h-0 flex-1 flex-col items-center justify-center px-4 pt-4 pb-12 sm:px-6 md:px-10 md:pt-8 md:pb-15 lg:px-12"
        data-lenis-prevent
      >
        {messages.length === 0 ? (
          <div className="flex w-full flex-1 flex-col items-center justify-center text-center md:-translate-y-20">
            <NovaMark state={hasDraft ? 'leanin' : 'breathing'} theme={theme} size={100} className="mb-2" />
            <NovaBetaTag theme={theme} height={22} className="-mt-2 mb-3" />
            <h1
              className="mb-4 max-w-[900px] font-sohne text-balance text-[30px] leading-[34px] font-semibold tracking-[-0.6px] md:mb-6 md:text-[44px] md:leading-[48px] md:tracking-[-1.2px]"
              style={{ color: surfaces.ink.heading }}
            >
              Hi {firstName}, ask me anything
            </h1>
            <div className="mb-6 max-w-[560px] text-base text-balance leading-[24px] md:mb-[34px] md:text-lg md:leading-[26px]" style={{ color: surfaces.ink.standfirst }}>
              Nova.ai answers with evidence, drawn only from Stylus intelligence.
            </div>
            <div className="w-[720px] max-w-full">
              <NovaComposer
                onSend={(query) => sendMessage(query)}
                onAttach={openUploadDialog}
                onAttachExistingDocument={(relativeName) => addPendingFilePaths([relativeName])}
                userProfileId={user?.amsUserId}
                waiting={waiting || streaming}
                streaming={streaming}
                onStop={stopStreaming}
                hasMessages={false}
                onDraftChange={handleDraftChange}
                variant="master"
                theme={theme}
                published12mFilter={published12mFilter}
                onTogglePublished12mFilter={setPublished12mFilter}
                pendingFilePaths={pendingFilePaths}
                onRemovePendingFilePath={removePendingFilePath}
              />
              <div className="mt-5 text-center text-sm text-balance md:mt-[26px]" style={{ color: surfaces.ink.placeholder }}>
                See{' '}
                <Link
                  link={{ href: DISCOVER_HREF, text: 'Discover' }}
                  className="no-underline transition-colors hover:text-[color:var(--nova-link-hover)]"
                  style={{ color: surfaces.ink.heading, ...novaTableVars(surfaces) } as React.CSSProperties}
                >
                  Discover
                </Link>{' '}
                for common queries and{' '}
                <Link
                  link={{ href: REPORT_GENERATOR_HREF, text: 'Build a Report' }}
                  className="no-underline transition-colors hover:text-[color:var(--nova-link-hover)]"
                  style={{ color: surfaces.ink.heading, ...novaTableVars(surfaces) } as React.CSSProperties}
                >
                  Build a Report
                </Link>{' '}
                for analyst-verified prompts.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              ref={scrollContainerRef}
              onScroll={handleTranscriptScroll}
              className="mt-2 mr-[-16px] mb-4 min-h-0 w-full flex-1 overflow-y-auto pr-4 sm:mr-[-24px] md:mt-9 md:mr-[-40px] md:mb-5 lg:mr-[-48px]"
              data-lenis-prevent
            >
              {/* Bleeds through the pane's own right padding (negative margin cancels it) then
                  reserves a small fixed 16px gap of its own, so the scrollbar sits close to the
                  pane's true edge without being flush against it — the content stays centered. */}
              <div className="mx-auto w-full max-w-[880px]">
                {turns.map((turn, index) => {
                  const isLastTurn = index === turns.length - 1;
                  return (
                    // Keyed by index (not content) so each turn keeps its own NovaAnswerCard
                    // instance — and its per-answer UI state (gallery expansion, like/dislike,
                    // copy toast) — as earlier turns stay on screen and only the last one updates.
                    // `conversationId` forces a fresh set of instances when switching
                    // conversations, so old per-card state doesn't leak into the new transcript.
                    <div
                      key={`${conversationId}-${index}`}
                      className={isLastTurn ? undefined : 'mb-10 border-b pb-10'}
                      style={isLastTurn ? undefined : { borderColor: surfaces.surfaces.hairline }}
                    >
                      {isLastTurn && <div ref={transcriptEndRef} />}
                      <NovaAnswerCard
                        question={turn.question}
                        message={turn.answer}
                        waiting={isLastTurn && waiting}
                        streaming={isLastTurn && streaming}
                        leanIn={isLastTurn && hasDraft}
                        followUpQuestions={isLastTurn ? suggestedQuestions : undefined}
                        onFollowUpClick={(question) => sendMessage(question)}
                        theme={theme}
                        published12mFilter={published12mFilter}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="w-full max-w-[880px]">
              <NovaComposer
                onSend={(query) => sendMessage(query)}
                onAttach={openUploadDialog}
                onAttachExistingDocument={(relativeName) => addPendingFilePaths([relativeName])}
                userProfileId={user?.amsUserId}
                waiting={waiting || streaming}
                streaming={streaming}
                onStop={stopStreaming}
                hasMessages
                onDraftChange={handleDraftChange}
                variant="master"
                theme={theme}
                published12mFilter={published12mFilter}
                onTogglePublished12mFilter={setPublished12mFilter}
                pendingFilePaths={pendingFilePaths}
                onRemovePendingFilePath={removePendingFilePath}
              />
            </div>
          </>
        )}

        <NovaUploadDialog
          open={uploadOpen}
          userProfileId={user?.amsUserId || ''}
          initialFiles={uploadInitialFiles}
          onClose={() => setUploadOpen(false)}
          onUploaded={(filePaths) => {
            addPendingFilePaths(filePaths);
            setUploadOpen(false);
          }}
        />

        <div
          className="pointer-events-none absolute right-0 bottom-0 left-0 px-4 pb-[18px] text-center text-xs md:text-sm"
          style={{ color: surfaces.ink.placeholder }}
        >
          {novaDisclaimer.short}
        </div>
      </div>
    </div>
  );
};

export default NovaLanding;
